import React, { useEffect, useState } from 'react'
import { Grid, GridColumn, Icon, Menu, MenuItem } from 'semantic-ui-react'
import { onSegmentAttempted, selectSegments, setField } from '../../state/slice/form'
import { useDispatch, useSelector } from 'react-redux'
import Footer from '../footer/Footer'
import { FORM_SEGMENTS } from '../../state/common/FORM_SEGMENTS'
import { useNavigate } from 'react-router-dom'
import { Button } from 'react-dxd-components'
import _ from 'lodash'
import REQUEST_STATUS from '../../state/common/REQUEST_STATUS'
import Loader from '../common/Loader'
import { fetchEoiRules } from '../../state/reducers/fetchEoiRules'
import toStateSpecialAbbreviation from '../../util/toStateSpecialAbbreviation'
import { submitForm } from '../../state/reducers/submitForm'
import validateFormReady from '../../util/validateFormReady'
import selectedCoverages from '../../util/selectedCoverages'
import { HEALTH_INFORMATION_SEGMENTS } from '../../constant/HEALTH_INFORMATION_SEGMENTS'

const hasPreviouslyFetchedHealthQuestions = (segments) => {
  return HEALTH_INFORMATION_SEGMENTS.filter(s => {
    return segments[s].enabled
  })?.length > 0
}

export default function FormRoot () {
  const dispatch = useDispatch()
  const {
    options,
    error,
    context: {
      appDataRequestStatus: status
    }
  } = useSelector(state => state.form)
  const segments = useSelector(selectSegments, _.isEqual)
  const verificationFieldsReady = useSelector(state => state.form.segments.verification.fields.ready.value)
  const canSubmit = verificationFieldsReady === 'true'
  const [activeSegment, setActiveSegment] = useState('employer')
  const navigate = useNavigate()
  const employerOptions = useSelector(state => state.form.segments.employer.fields)
  const formReady = validateFormReady(options, employerOptions)
  useEffect(() => {
    if (!formReady || error) {
      navigate('/home')
    } else {
      dispatch(setField('segments.employer.fields.groupNumber.disabled', true, true, false, false, false))
      dispatch(setField('segments.employer.fields.address.state.disabled', true, true, false, false, false))
    }
    if (employerOptions.address.state.value && employerOptions.groupNumber.value && error === undefined) {
      dispatch(fetchEoiRules({
        state: toStateSpecialAbbreviation(employerOptions.address.state.value, options.groupType.value),
        groupNumber: employerOptions.groupNumber.value,
        selectedCoverages: selectedCoverages(options)
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, options, dispatch, formReady, error])

  const onSegmentChange = (newSegment) => {
    dispatch(onSegmentAttempted(activeSegment))
    setActiveSegment(newSegment)
  }

  const previous = () => {
    onSegmentChange(Object.keys(segments)
      .filter(i => segments[i].enabled && segments[i].index < segments[activeSegment].index)
      .sort((a, b) => segments[a].index - segments[b].index).pop())
  }

  const next = () => {
    onSegmentChange(Object.keys(segments)
      .filter(i => segments[i].enabled && segments[i].index > segments[activeSegment].index)
      .sort((a, b) => segments[a].index - segments[b].index)[0])
  }

  const submit = () => {
    dispatch(submitForm())
    navigate('/thank-you')
  }

  /**
   * Need to also check if we've already fetched questions.
   * If we're re-fetching due to situs state change, don't show this loader again
   */
  if ([REQUEST_STATUS.NOT_STARTED, REQUEST_STATUS.IN_PROGRESS].includes(status) && !hasPreviouslyFetchedHealthQuestions(segments)) {
    return <Loader message='Loading health questions. This may take up to a minute' />
  }

  return <Grid className='container-lg'>
    <GridColumn computer={4} tablet={16} mobile={16}>
      <Menu
        data-testid='form-segments'
        stackable
        fluid
        vertical
        tabular
        className={segments[activeSegment].errored ? 'error-border' : ''}
      >
        {Object.keys(segments).map(segment => {
          return segments[segment].enabled && <MenuItem
            className={`form-segment${segments[segment].errored ? ' invalid' : ''}${segments[segment].completed ? ' valid' : ''}`}
            key={segment}
            name={segment}
            data-testid={`${segment}-tab`}
            active={activeSegment === segment}
            onClick={(e, { name }) => onSegmentChange(name)}
          >
            {FORM_SEGMENTS[segment].display}
            {segments[segment].errored && <Icon name='x' color='red' />}
            {segments[segment].completed && <Icon name='check' color='green' />}
          </MenuItem>
        })}
      </Menu>
    </GridColumn>
    <GridColumn stretched computer={12} tablet={16} mobile={16}>
      {FORM_SEGMENTS[activeSegment].panel}
      <Grid className='u-mY0'>
        {activeSegment !== 'employer' && <GridColumn computer={3} tablet={4} mobile={6} floated='left'>
          <Button fullWidth onClick={previous}>Previous</Button>
        </GridColumn>}
        {activeSegment !== 'verification' && <GridColumn computer={3} tablet={4} mobile={6} floated='right'>
          <Button fullWidth onClick={next}>Next</Button>
        </GridColumn>}
        {activeSegment === 'verification' && <GridColumn computer={3} tablet={4} mobile={6} floated='right'>
          <Button
            disabled={!canSubmit}
            className={[
              'Button--submit',
              !canSubmit && 'Button--submit--disabled'
            ].filter(Boolean).join(' ')}
            fullWidth
            onClick={submit}
          >
            Submit
          </Button>
        </GridColumn>}
      </Grid>
    </GridColumn>
    <Footer />
  </Grid>
}
